import { Button, Input, TableProps } from "antd";
import { capitalize } from "lodash";
import moment from "moment";
import { useState } from "react";
import { useParams } from "react-router-dom";
import * as XLSX from "xlsx";
import { useAppSelector } from "../../../../app/store";
import SearchIcon from "../../../../assets/icons/SearchIcon";
import CustomTable from "../../../../components/ui/CustomTable";
import { API_URL } from "../../../../constants/apiUrl";
import { APP_API } from "../../../../constants/constants";
import { useGetSingleBatchQuery } from "../../../../features/portal/batch/batchApi";
import { useGetAllVendorsQuery } from "../../../../features/portal/disbursementPayment/disbursementPaymentApi";
import useDownloadEmployeeXlsx from "../../../../hooks/useDownloadExcelWithApi";
import useError from "../../../../hooks/useError";
import useTable from "../../../../hooks/useTable";
import { formatDate, formatTime } from "../../../../lib/time";
import {
  GetAllDisbursementDataParamsType,
  GetAllVendorPaymentDataType,
} from "../../../../types/portal/disbursementPayment";
import { countParPageSerialNumber } from "../../../../utils/number";
import { addKeyInArray } from "../../../../utils/utils";

export default function PaidListTableVendor() {
  const [selectedData, setSelectedData] = useState<string[]>([]);
  const { loading: excelLoading, downloadXlsx } = useDownloadEmployeeXlsx();

  const {
    page,
    limit,
    handlePagination,
    handleFilter,
    handleLimit,
    filter,
    keyword,
    handleSearch,
  } = useTable<GetAllDisbursementDataParamsType>({
    defaultLimit: 1000,
  });
  const { user } = useAppSelector((state) => state.auth);
  const { id } = useParams();
  const roleCheck = localStorage.getItem("project_role");
  const {
    isLoading: isVendorLoading,
    isError: isVendorDetailsError,
    error: vendorDetailsError,
    data: vendorDetails,
    isFetching,
    refetch,
  } = useGetAllVendorsQuery({
    params: {
      ...filter,
      batch_id: id,
      company_id: user?.data?.company_id,
      page,
      limit,
      search: keyword,
    },
  });
  const {
    isError: singleBatchIsError,
    error: singleBatchError,
    data: singleBatchData,
    isLoading: singleBatchIsLoading,
  } = useGetSingleBatchQuery(id);
  useError(singleBatchIsError, singleBatchError, "Get single batch fail");
  useError(isVendorDetailsError, vendorDetailsError, "Get payment data fail");

  const singleBatch = singleBatchData?.data;

  const columns: TableProps<GetAllVendorPaymentDataType>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: <div className="whitespace-nowrap !w-[120px]">Vendor ID</div>,
      dataIndex: "vendor_id",
      render: (val) => (val ? val : "N/A"),
    },
    {
      title: <span className="whitespace-nowrap">Vendor Name</span>,
      dataIndex: "vendor_name",
      render: (val) => (val ? val : "N/A"),
    },
    {
      title: <span className="whitespace-nowrap">Invoice No</span>,
      dataIndex: "invoiceNo",
      render: (val) => (val ? val : "N/A"),
    },
    // {
    //   title: <span className="whitespace-nowrap">Department</span>,
    //   align: "center",
    //   dataIndex: "department",
    //   render: (val) => (val ? val : "N/A"),
    // },
    // {
    //   title: <span className="whitespace-nowrap">Designation</span>,
    //   dataIndex: "designation",
    //   render: (val) => (val ? val : "N/A"),
    // },
    {
      title: <span className="whitespace-nowrap">Amount (BDT)</span>,
      dataIndex: "amount",
      align: "center",
      render: (val: string | number) => (
        <>
          ৳{" "}
          {val
            ? Number(val)
                .toFixed(2)
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            : "0"}
        </>
      ),
    },
    {
      title: <span className="whitespace-nowrap">Wallet Number</span>,
      dataIndex: "wallet_name",
      render: (val) => (val ? val : "N/A"),
    },
    {
      title: <span className="whitespace-nowrap">Bank Name</span>,
      dataIndex: "bank_name",
      render: (val) => (val ? val : "N/A"),
    },
    {
      title: <span className="whitespace-nowrap">Account Title</span>,
      dataIndex: "account_title",
      render: (val) => (val ? val : "N/A"),
    },
    {
      title: <span className="whitespace-nowrap">Account Number</span>,
      dataIndex: "account_number",
      render: (val) => (val ? val : "N/A"),
    },
    {
      title: <span className="whitespace-nowrap">Branch Name</span>,
      dataIndex: "branch_name",
      render: (val) => (val ? val : "N/A"),
    },
    {
      title: <span className="whitespace-nowrap">Routing Number</span>,
      dataIndex: "routing_number",
      render: (val) => (val ? val : "N/A"),
    },
    {
      title: <span className="whitespace-nowrap">Transaction ID</span>,
      dataIndex: "transaction_id",
      render: (val) => (val ? val : "N/A"),
    },
    {
      title: <span className="whitespace-nowrap">Transaction Date</span>,
      dataIndex: "transaction_date",
      render: (val: string) =>
        val ? (
          <>
            {formatDate(val)}, {formatTime(val)}
          </>
        ) : (
          "N/A"
        ),
    },

    {
      title: <span className="whitespace-nowrap">Created At</span>,
      align: "center",
      dataIndex: "created_at",
      render: (val: string) =>
        val ? (
          <>
            {formatDate(val)}, {formatTime(val)}
          </>
        ) : (
          "N/A"
        ),
    },
    {
      title: <span className="whitespace-nowrap">Updated At</span>,
      align: "center",
      dataIndex: "updated_at",
      render: (val: string) =>
        val ? (
          <>
            {formatDate(val)}, {formatTime(val)}
          </>
        ) : (
          "N/A"
        ),
    },
    {
      title: <span className="whitespace-nowrap">Narration</span>,
      dataIndex: "narration",
      render: (val) => (val ? val : "N/A"),
    },
    {
      title: <div className="whitespace-nowrap !w-[110px]">Current Stage</div>,
      dataIndex: "current_stage",
      align: "center",
      render: (val) => (
        <span
          className={`px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-700`}
        >
          {val.charAt(0).toUpperCase() + val.slice(1)}
        </span>
      ),
    },
    {
      title: <div className="whitespace-nowrap !w-[110px]">Status</div>,
      dataIndex: "status",
      align: "center",
      render: (val) => (
        <span
          className={`px-2 py-1 rounded text-xs font-medium ${
            val === "Pending"
              ? "bg-yellow-100 text-yellow-700"
              : "bg-green-100 text-green-700"
          }`}
        >
          {val || "" ? capitalize(val) : "N/A"}
        </span>
      ),
    },
  ];

  const totalAmount = vendorDetails?.data?.data?.reduce(
    (sum, item) => sum + Number(item?.amount || 0),
    0
  );

  const exportToExcel = () => {
    const rawData = vendorDetails?.data?.data;

    // Column headers
    const columns = {
      "Vendor ID": "",
      "Vendor Name": "",
      "Invoice No": "",
      Amount: "",
      "Wallet Number": "",
      "Bank Name": "",
      "Account Title": "",
      "Account Number": "",
      "Branch Name": "",
      "Routing Number": "",
      "Transaction ID": "",
      "Transaction Date": "",
      Narration: "",
      "Current Stage": "",
      Status: "",
    };

    let formattedData: any[] = [];

    if (rawData?.length) {
      formattedData = rawData.map((item: any) => ({
        "Vendor ID": item?.vendor_id || "-",
        "Vendor Name": item?.vendor_name || "-",
        "Invoice No": item?.invoiceNo || "-",
        Amount: item?.amount || "-",
        "Wallet Number": item?.wallet_name || "-",
        "Bank Name": item?.bank_name || "-",
        "Account Title": item?.account_title || "-",
        "Account Number": item?.account_number || "-",
        "Branch Name": item?.branch_name || "-",
        "Routing Number": item?.routing_number || "-",
        "Transaction ID": item?.transaction_id || "-",
        "Transaction Date": item?.transaction_date || "-",
        Narration: item?.narration || "-",
        "Current Stage": item?.current_stage || "-",
        Status: item?.status || "-",
      }));
    } else {
      // Push one empty row to ensure headers appear
      formattedData.push(columns);
    }

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Employees");

    XLSX.writeFile(
      workbook,
      `Vendor_Payment_${singleBatch?.batchName}_${rawData?.length}.xlsx`
    );
  };

  const handleDownloadPaidList = () => {
    downloadXlsx(
      APP_API + API_URL.getAllVendors,
      {
        ...filter,
        search: keyword,
        skip: 0,
        limit: 100000000000,
        page: page,
      },
      `Vendor_Payment_${singleBatch?.batchName}_${moment().format(
        "DD MMMM YYYY"
      )}_${moment().format("h.mm.ss A")}`,
      "data",
      (data: GetAllVendorPaymentDataType["data"]["data"][0]) => {
        return {
          "Vendor ID": data?.vendor_id || "-",
          "Vendor Name": data?.vendor_name || "-",
          "Invoice No": data?.invoiceNo || "-",
          Amount: data?.amount || "-",
          "Wallet Number": data?.wallet_name || "-",
          "Bank Name": data?.bank_name || "-",
          "Account Title": data?.account_title || "-",
          "Account Number": data?.account_number || "-",
          "Branch Name": data?.branch_name || "-",
          "Routing Number": data?.routing_number || "-",
          "Transaction ID": data?.transaction_id || "-",
          "Transaction Date": data?.transaction_date || "-",
          Narration: data?.narration || "-",
          "Current Stage": data?.current_stage || "-",
          Status: data?.status || "-",
        };
      }
    );
  };

  return (
    <>
      <div>
        <div className="flex justify-between items-center mb-3">
          <div className="justify-start text-zinc-700 text-xl font-semibold font-['Inter'] leading-loose">
            Paid List
          </div>
          <Button
            disabled={!vendorDetails?.data?.data?.length}
            onClick={exportToExcel}
            className="h-9 px-3.5 py-2 !bg-white rounded-md outline outline-1 outline-offset-[-1px] outline-blue-500 inline-flex justify-center items-center gap-2.5 overflow-hidden disabled:cursor-not-allowed"
          >
            <span className="text-blue-600 text-sm font-semibold">
              Download
            </span>
          </Button>
        </div>
        <CustomTable
          loading={isVendorLoading}
          limit={limit}
          page={page}
          columns={columns}
          total={vendorDetails?.data?.count}
          dataSource={addKeyInArray(vendorDetails?.data?.data)}
          handleLimit={handleLimit}
          handlePagination={handlePagination}
          // rowSelection={{
          //   type: "checkbox",
          //   selectedRowKeys: selectedData,
          //   onChange: (key) => {
          //     setSelectedData(key as string[]);
          //   },
          //   getCheckboxProps: (record) => {
          //     return {
          //       disabled: record?.status === "invited",
          //     };
          //   },
          //   columnWidth: 40,
          // }}
          filterSection={
            <>
              {roleCheck === "Maker" && (
                <div className="px-3 py-1 bg-violet-50 rounded-[100px] inline-flex justify-center items-center gap-2.5">
                  <div className="justify-start text-blue-600 text-base font-medium">
                    Total Amount: ৳{" "}
                    {Number(totalAmount)
                      .toFixed(2)
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ",") || "0"}
                  </div>
                </div>
              )}

              <Input
                suffix={<SearchIcon />}
                placeholder="Search ID, Name, Wallet, Ac. Number"
                style={{ width: 317 }}
                allowClear
                onChange={handleSearch}
              />
            </>
          }
        />
      </div>
    </>
  );
}
